import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, DownloadSimple, ShareNetwork } from "@phosphor-icons/react";

const DISMISS_KEY = "sliply_install_dismissed";

function isStandalone() {
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

export default function InstallBanner() {
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
  const [ready, setReady] = useState(!!window.__sliplyInstall);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isStandalone() || sessionStorage.getItem(DISMISS_KEY)) return;

    const onReady = () => { setReady(true); setShow(true); };
    const onInstalled = () => setShow(false);
    window.addEventListener("sliply-installable", onReady);
    window.addEventListener("sliply-installed", onInstalled);
    window.addEventListener("appinstalled", onInstalled);

    // Already captured before mount (Chrome/Edge/Android)
    if (window.__sliplyInstall) { setReady(true); setShow(true); }
    // iOS Safari never fires beforeinstallprompt — show manual hint shortly after entry
    let t;
    if (isIOS) t = setTimeout(() => setShow(true), 1200);

    return () => {
      window.removeEventListener("sliply-installable", onReady);
      window.removeEventListener("sliply-installed", onInstalled);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, [isIOS]);

  const dismiss = () => { setShow(false); sessionStorage.setItem(DISMISS_KEY, "1"); };

  const install = async () => {
    const e = window.__sliplyInstall;
    if (!e) return;
    e.prompt();
    try { await e.userChoice; } catch {}
    window.__sliplyInstall = null;
    dismiss();
  };

  if (!show || (!ready && !isIOS)) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ type: "spring", stiffness: 300, damping: 26 }}
          data-testid="install-banner"
          className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-[calc(env(safe-area-inset-bottom)+88px)] lg:pb-6 pointer-events-none"
        >
          <div className="pointer-events-auto mx-auto max-w-md rounded-2xl bg-riq-primary text-white shadow-2xl shadow-riq-primary/30 p-4 flex items-center gap-3 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.07] riq-noise" />
            <div className="relative w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0 p-2">
              <img src="/sliply-mark.png" alt="Sliply" className="w-full h-full object-contain" />
            </div>
            <div className="relative flex-1 min-w-0">
              <div className="font-head font-semibold text-sm">Install Sliply</div>
              {isIOS && !ready ? (
                <p className="text-xs text-white/80 leading-snug mt-0.5 flex items-center gap-1 flex-wrap">
                  Tap <ShareNetwork size={13} weight="bold" className="inline" /> Share, then <b>“Add to Home Screen”</b>
                </p>
              ) : (
                <p className="text-xs text-white/80 leading-snug mt-0.5">Add it to your home screen for one-tap scanning.</p>
              )}
            </div>
            {ready && (
              <button
                data-testid="install-banner-btn"
                onClick={install}
                className="relative shrink-0 rounded-full bg-white text-riq-primary px-4 py-2 text-sm font-semibold flex items-center gap-1.5 hover:-translate-y-0.5 transition-transform"
              >
                <DownloadSimple size={16} weight="bold" /> Install
              </button>
            )}
            <button data-testid="install-banner-dismiss" onClick={dismiss} className="relative shrink-0 p-1 text-white/70 hover:text-white">
              <X size={18} />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
